/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * How a tone LOOKS (plan-voorwaarden-vergelijking-2026-10-07 §6.0): the one
 * tone → class map every terms component reads — the row's two lines, the
 * fact table's values, the bullet dots. The tone itself is decided once, in
 * src/lib/termsTone.ts; this file only paints it. Colour marks a deviation from
 * what most offers do, in either direction:
 *
 *   good     emerald — in the visitor's favour            (→ a pluspunt)
 *   watch    amber   — a catch                            (→ an aandachtspunt)
 *   neutral  slate   — the market norm, or prose          (→ no bullet)
 *   unknown  muted   — not stated, or not checked         (→ a "niet gecontroleerd /
 *                      niet vermeld" aandachtspunt; never a warning colour)
 *
 * Light mode, the way the rest of the comparer does it (the set-filter word in
 * FilterSelect.tsx): the `[html.light_&]:` variant with a literal hex. The
 * emerald-300 / amber-300 TOKENS are remapped for the light page (#047857 /
 * #b45309) but only reach 5.0:1 / 4.6:1 on the light row (#f1f5f9) at 11–12 px;
 * the literal emerald-800 (#065f46, 7.0:1) and amber-800 (#92400e, 6.5:1 — 7.1:1
 * on white) are the darker pair. Dark: emerald-300 9.6:1, amber-300 10.2:1 on
 * the slate-800 row. Neutral rides the slate-200 token (#1e293b on light,
 * 13.4:1) and muted the `fg-muted` semantic token (#94a3b8 dark 5.7:1, #475569
 * light 6.9:1).
 *
 * Tone is colour ONLY (D5): the WORDS carry the fact, and an sr-only
 * "(gunstig)" / "(let op)" (`offer_terms.tone.*`) carries the judgement, so
 * colour is never the only signal (WCAG 1.4.1).
 */

import type { FactTone } from '../../lib/termsTone';

/** Text colour per tone. Every string is complete, so Tailwind finds the classes. */
export const TONE_TEXT: Readonly<Record<FactTone, string>> = {
  good: 'text-emerald-300 [html.light_&]:text-[#065f46]',
  watch: 'text-amber-300 [html.light_&]:text-[#92400e]',
  neutral: 'text-slate-200',
  unknown: 'text-fg-muted',
};

/**
 * The bullet dot per tone: a filled emerald dot is a pluspunt, a filled amber
 * one an aandachtspunt, a HOLLOW muted ring the "niet gecontroleerd / niet
 * vermeld" family — absence reads as absence, not as a warning. Amber's light
 * twin is the literal amber-700 #b45309 (4.6:1 on the light row #f1f5f9): the
 * vivid amber-500 dot was 1.95:1, under the 3:1 a non-text mark needs (WCAG
 * 1.4.11). `neutral` earns no bullet of a toned fact; its dot is the offer's
 * verdienmodel point (termsVerdict), a fact that explains rather than warns.
 */
export const TONE_DOT: Readonly<Record<FactTone, string>> = {
  good: 'bg-emerald-500',
  watch: 'bg-amber-400 [html.light_&]:bg-[#b45309]',
  neutral: 'bg-slate-400',
  unknown: 'border-[1.5px] border-fg-muted',
};

/**
 * The sr-only judgement per tone: an i18n key under `offer_terms.tone.*`, or
 * absent where there is nothing to add (neutral is the norm, muted already says
 * "not checked" in words).
 */
export const TONE_WORD_KEY: Readonly<Partial<Record<FactTone, string>>> = {
  good: 'offer_terms.tone.good',
  watch: 'offer_terms.tone.watch',
};
